import {GameRoundPresentation} from "@nolimitcity/slot-game/bin/timelinegameround/core/gameround/GameRoundPresentation";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {BookOfShadowsParsedGameData} from "../../server/data/BookOfShadowsParsedGameData";
import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {BetLineEvent} from "@nolimitcity/slot-game/bin/core/betline/event/BetLineEvent";
import {BookOfShadowsGameModuleConfig} from "../../BookOfShadowsGameModuleConfig";
import {StateMatrix} from "../../utils/StateMatrix";
import {SymbolUtil} from "../../utils/SymbolUtil";
import {BookOfShadowsGameConfig, IBookOfShadowsWinRatioConfig} from "../../BookOfShadowsGameConfig";
import {BookOfShadowsScatterWinView} from "./BookOfShadowsScatterWinView";
import {BigWinStep} from "../bigWin/BigWinStep";
import {SoundManager} from "../../sound/SoundManager";
import {BookOfShadows} from "../../BookOfShadows";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";

export class BookOfShadowsScatterWinController implements GameRoundPresentation {
    private _hasWin: boolean;
    private _fsSymbolPositionReels: number[] = [];
    private _fsSpecialSym: string;
    private _fsActiveRows: number;
    private _scatterWins: IBetLineWinData[] = [];
    private _symbolStates: StateMatrix<string>;
    private readonly _view: BookOfShadowsScatterWinView;
    private readonly _bigWinLevel0: BigWinStep;

    private _transformTl: TimelineLite;

    /**
     * @constructs
     */
    constructor() {
        this._view = new BookOfShadowsScatterWinView();
        this._bigWinLevel0 = new BigWinStep(BookOfShadowsGameConfig.instance.WIN_RATIOS.normalWin[0], BookOfShadowsGameConfig.instance.WIN_RATIOS.bigWin[0]);
    }

    /**
     * @inheritDoc
     * @param data
     */
    public prepare(data: BookOfShadowsParsedGameData): void {
        this._hasWin = data.scatterWins.length > 0;
        this._scatterWins = data.scatterWins;

        this._fsSymbolPositionReels = [];
        for (let i = 0; i < data.fsSymbolPosition.length; i++) {
            if (data.fsSymbolPosition[i] > -1) {
                this._fsSymbolPositionReels.push(i);
            }
        }

        this._fsSpecialSym = data.fsSpecialSymbol;
        this._fsActiveRows = data.selectedRowsFS;

        this._symbolStates = SymbolUtil.createSymbolStateMatrixFromBetWins(
            <IBetLineWinData[]>data.betWins!,
            BookOfShadowsGameConfig.instance.SYMBOL_STATES.noWin!.keyword,
            BookOfShadowsGameConfig.instance.SYMBOL_STATES.win!.keyword);
    }

    /**
     * @inheritDoc
     * @param data
     */
    public getAnimation(data: BookOfShadowsParsedGameData): gsap.SimpleTimeline | gsap.TimelineLite | gsap.TimelineMax | null {
        if (!this._hasWin) {
            return null;
        }

        let tl: TimelineLite = new TimelineLite(),
            transformTl: TimelineLite = this.transformSymbols();

        // removing the win amount from the screen.
        if (data.betWins.length > 0) {
            // adding no win for already won symbols in betLines data.
            tl.add(this.getNoWinSymbolAnimations());
        }

        // transforming the symbols.
        tl.add(transformTl);
        tl.add(TweenLite.to(() => {
        }, transformTl.totalDuration(), {}), data.betWins.length > 0 ? 0.4 : 0);

        // #368
        const displayIndividualWinsInGame: boolean = BookOfShadows.api.gameClientConfiguration.displayIndividualWinsInGame;
        if (displayIndividualWinsInGame) {
            tl.add(BookOfShadowsGameModuleConfig.betLineWinAmountView.showBetlineAmountInitialWin(data.scatterWins));
        }

        // displaying the betLines.
        tl.add(this.displayBetLines(data));

        this._transformTl = transformTl;

        return tl;
    }

    /**
     * @inheritDoc
     * @param isActive
     * @param data
     */
    public abort(isActive: boolean, data: BookOfShadowsParsedGameData): gsap.SimpleTimeline | gsap.TimelineLite | gsap.TimelineMax | null {
        let tl: TimelineLite = new TimelineLite(),
            sym: StateReelSymbol,
            reelId: number;

        const maxRows: number = 5,
            startJ: number = maxRows - this._fsActiveRows;

        // #368
        StageManager.getLayer(BookOfShadowsGameConfig.instance.LAYERS.INITIAL_WIN_AMOUNT_BET_LINES.name).removeChildren();

        tl.add(() => {
            EventHandler.dispatchEvent(new GameEvent(BetLineEvent.HIDE_ALL_BET_LINES));

            for (let i = 0; i < this._fsSymbolPositionReels.length; i++) {
                reelId = this._fsSymbolPositionReels[i];
                for (let j: number = startJ; j < maxRows; ++j) {
                    sym = Reels.getSymbol(reelId, j)!;
                    if (sym) {
                        sym.replaceWith(this._fsSpecialSym + "Gold", reelId, BookOfShadowsGameConfig.instance.SYMBOL_STATES.goldNormal);
                    }
                }
            }

            if ((!this._transformTl) || (this._transformTl.isActive() && this._transformTl.totalProgress() < 1)) {
                SoundManager.playFSNewSymbols();
            }

            this._bigWinLevel0.prepare(data);
            this._bigWinLevel0.abort(isActive);

            // #276 fix
            BookOfShadowsGameModuleConfig.countUpView.soundPlayingInScatterWinAbort();
        }, 0);

        return tl;
    }

    /**
     * @description This method will set the no win state for those symbols which were participating in the win in the betLineWin data.
     */
    private getNoWinSymbolAnimations(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        tl.add(() => {
            for (let reelId = 0; reelId < BookOfShadowsGameConfig.instance.REELS_NUM; reelId++) {
                const reelCoords: [number, number][] = this._symbolStates.getCoordinatesWithStateInReel(reelId, BookOfShadowsGameConfig.instance.SYMBOL_STATES.win!.keyword);
                for (let coords of reelCoords) {
                    this.changeStateOfSymbols(coords[0], coords[1], BookOfShadowsGameConfig.instance.SYMBOL_STATES.noWin!);
                }
            }
        }, 0);

        return tl;
    }

    /**
     * @description To change the state of symbols.
     * @param reelId
     * @param symbolId
     * @param state
     */
    private changeStateOfSymbols(reelId: number, symbolId: number, state: ISymbolStateConfig): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const symbol: StateReelSymbol | null = Reels.getSymbol(reelId, symbolId);
        if (symbol) {
            // #189
            if (symbol.symName === "S") {
                return tl;
            }

            const symbolAnimation: Timeline = symbol.changeState({
                state: state,
                setStack: true
            })!;

            tl.add(symbolAnimation);
        }

        return tl;
    }

    /**
     * @description This method calls the transformation of the symbols.
     */
    private transformSymbols(): TimelineLite {
        const maxRows: number = 5,
            timer: number = 0.2,
            startJ: number = maxRows - this._fsActiveRows;

        let tl: TimelineLite = new TimelineLite(),
            reelId: number,
            startTime: number;
        tl.delay(0.3);

        for (let i = 0; i < this._fsSymbolPositionReels.length; i++) {
            reelId = this._fsSymbolPositionReels[i];
            for (let j: number = startJ; j < maxRows; ++j) {
                startTime = (i * (timer * (maxRows - startJ)) + (timer * j));
                tl.add(this._view.playAnim.bind(this._view, reelId, j, this._fsSpecialSym + "Gold"), startTime);
                tl.add(TweenLite.to(() => {
                }, timer, {}), startTime);
            }
        }

        return tl;
    }

    /**
     * @description To display the betLines one by one.
     * @param data
     */
    private displayBetLines(data: BookOfShadowsParsedGameData): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        const time: number = (3.5 / this._scatterWins.length);

        for (let i = 0; i < this._scatterWins.length; i++) {
            tl.add(() => {
                EventHandler.dispatchEvent(new GameEvent(BetLineEvent.SHOW_WIN_BET_LINE, this._scatterWins[i].betLineIndex + 1));
                SoundManager.playFSLine((i + 1));
            }, i * time);

            // #75
            if (i === (Math.ceil(this._scatterWins.length / 2) - 1)) {
                tl.add(this.playCountUp(data), i * time);
            }
        }

        tl.add(() => EventHandler.dispatchEvent(new GameEvent(BetLineEvent.HIDE_ALL_BET_LINES)));

        return tl;
    }

    /**
     * @description To play the count up or big win level 0.
     * #75 implementation.
     * @param data
     */
    private playCountUp(data: BookOfShadowsParsedGameData): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (data.isBigWin) {
            tl.add([
                () => this._bigWinLevel0.prepare(data),
                () => this._bigWinLevel0.getAnimation(data)
            ]);
        } else {
            const duration: number = data.currentWinRatioConfig!.duration;
            const countStart: TimelineLite = BookOfShadowsGameModuleConfig.countUpView.getWinStepAnimation(0, data.singleWin, duration, false);

            tl.add([
                countStart,
                () => {
                    BookOfShadowsGameModuleConfig.countUpView.playAnimOnNormalWin();

                    if (duration > 1) {
                        BookOfShadowsGameModuleConfig.miniCoinShower.playMiniShower();
                    }

                    SoundManager.playWin((data.currentWinRatioConfig! as IBookOfShadowsWinRatioConfig).sound!);
                }
            ]);
        }

        return tl;
    }
}